import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const CustomDomainSettings = () => {
  const [domainData, setDomainData] = useState({
    customDomain: '',
    subdomain: 'accountech',
    sslEnabled: true,
    redirectEnabled: true
  });

  const [verificationStatus, setVerificationStatus] = useState('pending'); // pending, verified, failed
  const [dnsRecords, setDnsRecords] = useState([
    {
      type: 'CNAME',
      name: 'www',
      value: 'accountech.app.domain.com',
      status: 'pending'
    },
    {
      type: 'A',
      name: '@',
      value: '76.76.19.125',
      status: 'verified'
    }
  ]);

  const handleDomainChange = (field, value) => {
    setDomainData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const verifyDomain = () => {
    console.log('Verifying domain:', domainData?.customDomain);
    // TODO: Implement domain verification logic
    setVerificationStatus('verified');
  };

  const generateSSLCertificate = () => {
    console.log('Generating SSL certificate for:', domainData?.customDomain);
    // TODO: Implement SSL certificate generation
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Custom Domain Settings</h2>
          <p className="text-muted-foreground">Configure a custom domain for your organization's account portal</p>
        </div>
        <Button iconName="Save">
          Save Changes
        </Button>
      </div>
      {/* Current Setup */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium text-foreground mb-4">Current Setup</h3>
        <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
          <div>
            <p className="font-medium text-foreground">Default Subdomain</p>
            <p className="text-sm text-muted-foreground">accountech.accountech-ai.com</p>
          </div>
          <div className="flex items-center space-x-2">
            <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
              Active
            </span>
            <Button variant="outline" size="sm" iconName="ExternalLink">
              Visit
            </Button>
          </div>
        </div>
      </div>
      {/* Custom Domain Configuration */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium text-foreground mb-6">Custom Domain Configuration</h3>
        
        <div className="space-y-6">
          <Input
            label="Custom Domain"
            value={domainData?.customDomain}
            onChange={(e) => handleDomainChange('customDomain', e?.target?.value)}
            placeholder="portal.yourcompany.com"
            description="Enter the custom domain you want to use for your account portal"
          />

          <Input
            label="Subdomain Prefix"
            value={domainData?.subdomain}
            onChange={(e) => handleDomainChange('subdomain', e?.target?.value)}
            placeholder="accountech"
            description="This will be used for the default subdomain: subdomain.accountech-ai.com"
          />

          <div className="flex items-center space-x-4">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={domainData?.sslEnabled}
                onChange={(e) => handleDomainChange('sslEnabled', e?.target?.checked)}
                className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
              <span className="text-sm font-medium text-foreground">Enable SSL/TLS Certificate</span>
            </label>

            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={domainData?.redirectEnabled}
                onChange={(e) => handleDomainChange('redirectEnabled', e?.target?.checked)}
                className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
              <span className="text-sm font-medium text-foreground">Redirect WWW to Non-WWW</span>
            </label>
          </div>
        </div>
      </div>
      {/* DNS Configuration */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium text-foreground mb-6">DNS Configuration</h3>
        
        <div className="mb-6">
          <div className={`flex items-center space-x-2 p-3 rounded-lg ${
            verificationStatus === 'verified' 
              ? 'bg-green-50 border border-green-200' 
              : verificationStatus === 'failed' ?'bg-red-50 border border-red-200' :'bg-yellow-50 border border-yellow-200'
          }`}>
            <Icon 
              name={verificationStatus === 'verified' ? 'CheckCircle' : verificationStatus === 'failed' ? 'XCircle' : 'Clock'} 
              size={16} 
              className={
                verificationStatus === 'verified' 
                  ? 'text-green-600' 
                  : verificationStatus === 'failed' ?'text-red-600' :'text-yellow-600'
              }
            />
            <span className={`text-sm font-medium ${
              verificationStatus === 'verified' 
                ? 'text-green-700' 
                : verificationStatus === 'failed' ?'text-red-700' :'text-yellow-700'
            }`}>
              {verificationStatus === 'verified' 
                ? 'Domain verified successfully' 
                : verificationStatus === 'failed' ?'Domain verification failed' :'Domain verification pending'
              }
            </span>
          </div>
        </div>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground mb-4">
            Add the following DNS records to your domain registrar to verify ownership and enable custom domain access.
          </p>

          <div className="overflow-x-auto">
            <table className="w-full border border-border rounded-lg">
              <thead className="bg-muted/30">
                <tr>
                  <th className="text-left p-3 text-sm font-medium text-foreground">Type</th>
                  <th className="text-left p-3 text-sm font-medium text-foreground">Name</th>
                  <th className="text-left p-3 text-sm font-medium text-foreground">Value</th>
                  <th className="text-left p-3 text-sm font-medium text-foreground">Status</th>
                  <th className="text-left p-3 text-sm font-medium text-foreground">Action</th>
                </tr>
              </thead>
              <tbody>
                {dnsRecords?.map((record, index) => (
                  <tr key={index} className="border-t border-border">
                    <td className="p-3">
                      <span className="px-2 py-1 text-xs font-medium bg-muted text-foreground rounded">
                        {record?.type}
                      </span>
                    </td>
                    <td className="p-3 text-sm font-mono text-foreground">{record?.name}</td>
                    <td className="p-3 text-sm font-mono text-foreground">{record?.value}</td>
                    <td className="p-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        record?.status === 'verified' 
                          ? 'bg-green-100 text-green-700' :'bg-yellow-100 text-yellow-700'
                      }`}>
                        {record?.status}
                      </span>
                    </td>
                    <td className="p-3">
                      <Button variant="ghost" size="sm" iconName="Copy">
                        Copy
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <Button variant="outline" onClick={verifyDomain} iconName="RefreshCw">
            Check DNS
          </Button>
          {domainData?.sslEnabled && (
            <Button onClick={generateSSLCertificate} iconName="Shield">
              Generate SSL Certificate
            </Button>
          )}
        </div>
      </div>
      {/* Domain Management */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium text-foreground mb-6">Domain Management</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 border border-border rounded-lg text-center">
            <Icon name="Globe" size={24} className="mx-auto mb-2 text-muted-foreground" />
            <h4 className="font-medium text-foreground mb-1">Domain Status</h4>
            <p className="text-sm text-muted-foreground">
              {verificationStatus === 'verified' ? 'Active & Verified' : 'Pending Verification'}
            </p>
          </div>

          <div className="p-4 border border-border rounded-lg text-center">
            <Icon name="Shield" size={24} className="mx-auto mb-2 text-muted-foreground" />
            <h4 className="font-medium text-foreground mb-1">SSL Certificate</h4>
            <p className="text-sm text-muted-foreground">
              {domainData?.sslEnabled ? 'Enabled' : 'Disabled'}
            </p>
          </div>

          <div className="p-4 border border-border rounded-lg text-center">
            <Icon name="ArrowRight" size={24} className="mx-auto mb-2 text-muted-foreground" />
            <h4 className="font-medium text-foreground mb-1">Redirects</h4>
            <p className="text-sm text-muted-foreground">
              {domainData?.redirectEnabled ? 'Configured' : 'Disabled'}
            </p>
          </div>
        </div>
      </div>
      {/* Action Buttons */}
      <div className="flex justify-end space-x-4 pt-6 border-t border-border">
        <Button variant="outline">
          Reset Configuration
        </Button>
        <Button iconName="Save">
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default CustomDomainSettings;